import { useNewLeaversInvestorsTableController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/NewLeaversSteps/NewLeaversInvestors/NewLeaversInvestorsTable/useNewLeaversInvestorsTableController';
import {
	NewLeaverContract,
	NewLeaverInvestorLocal,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted';
import { NumberFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/NumberFormatted';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { TableSecurityTagPair } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/common';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Cell, HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { isInvalidTableTextCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/defautTableUtils';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { TableCheckbox } from '@dealstack/frontend/shared/components/Table/DefaultTable/TableComponents';
import { TableHeaderContractCellGroup } from '@dealstack/frontend/shared/components/TableHeaderContractCellGroup/TableHeaderContractCellGroup';
import { TextWithTooltip } from '@dealstack/frontend/shared/components/TextWithTooltip/TextWithTooltip';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import { Stack, Text as TextMantine, Tooltip as TooltipMantine } from '@mantine/core';
import React, { Dispatch, memo, SetStateAction, useMemo } from 'react';
import styled from 'styled-components';

import { ITextProps } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { Align } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell.types';
import { SideAgreementsPopup } from './SideAgreementsPopup';

interface Props {
	className?: string;
	investorsList: NewLeaverInvestorLocal[];
	setInvestorsList: Dispatch<SetStateAction<NewLeaverInvestorLocal[]>>;
}

const NewLeaversInvestorsTable = ({ investorsList, setInvestorsList, className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const tMipManage = useTranslationMipManage();
	const {
		sideAgreements,
		mainContracts,
		currency,
		onUpdateInvestorContracts,
		totalSideAgreementsAssigned,
		securitiesList,
		attachToAllInvestorCheckboxStateArray: contractsAllSelection,
		loanDefinitions,
	} = useNewLeaversInvestorsTableController(investorsList, setInvestorsList);

	const onSelectAllClicked = (contractProp: NewLeaverContract): void => {
		if (!investorsList) {
			return;
		}
		const isContractSelectedForEveryone =
			contractsAllSelection?.find(
				(itemContract) => itemContract.contractID === contractProp.id
			)?.isChecked ?? false;
		const updatedInvestorList = investorsList?.map((investor) => {
			const updatedContracts = isContractSelectedForEveryone
				? investor.contracts?.filter(
						(investorContractID) => investorContractID !== contractProp.id
					)
				: [...(investor.contracts || []), contractProp.id];
			return { ...investor, contracts: updatedContracts };
		});
		setInvestorsList(updatedInvestorList);
	};
	const getLoanDefinitionTypeTranslation = useSecurityTypeTranslation();

	return (
		<div className={className}>
			<DefaultTable maxSize>
				<HeaderRow>
					<HeaderCell
						size={2}
						sectionSeparator
						groupLabel={tMipCreate('investors.table.header.name')}
						sectionLabel={tMipCreate('investors.table.header.individual')}
						sectionIcon="person"
					>
						{tMipCreate('investors.table.header.first_name')}
					</HeaderCell>
					<HeaderCell size={2}>
						{tMipCreate('investors.table.header.last_name')}
					</HeaderCell>
					<HeaderCell size={4} groupLabel={tMipCreate('investors.table.header.contacts')}>
						{tMipCreate('investors.table.header.email')}
					</HeaderCell>

					{securitiesList?.length
						? securitiesList?.map((security, index) => {
								const isFirst = index === 0;
								return (
									<React.Fragment
										key={`${security.id}${security.name}${security.type}`}
									>
										<HeaderCell
											size={1.8}
											groupComponent={
												<TableSecurityTagPair
													size={5.8} // Combination of three cells of sizes 1.8 + 2 + 2.
													name={security.name}
													type={security.type}
												/>
											}
											sectionSeparator={isFirst}
											sectionLabel={
												isFirst
													? tMipCreate(
															'investors.table.header.securities'
														)
													: undefined
											}
											childrenAlign="left"
											sectionIcon="bank"
										>
											{tMipCreate('investors.table.header.own_number')}
										</HeaderCell>
										<HeaderCell size={2} childrenAlign="left">
											{tMipCreate('investors.table.header.number_sold')}
										</HeaderCell>
										<HeaderCell size={2} childrenAlign="left">
											{tMipCreate('investors.table.header.total_price')}
										</HeaderCell>
									</React.Fragment>
								);
							})
						: null}

					{loanDefinitions?.length
						? loanDefinitions?.map((loanDefinition, index) => {
								const isFirst = index === 0;
								const typeTranslation = getLoanDefinitionTypeTranslation(
									loanDefinition.type
								);
								return (
									<React.Fragment
										key={`${loanDefinition.id}${loanDefinition.name}${loanDefinition.type}`}
									>
										<HeaderCell
											size={1.8}
											groupComponent={
												<Stack gap={2}>
													<TooltipMantine label={typeTranslation}>
														<TextMantine
															size="B6Medium"
															c={defaultTheme.v2.color.neutral[600]}
															truncate={true}
															lineClamp={1}
															style={{ wordBreak: 'break-all' }}
														>
															{typeTranslation}
														</TextMantine>
													</TooltipMantine>
													<TooltipMantine label={loanDefinition.name}>
														<TextMantine
															size="B6Bold"
															truncate={true}
															lineClamp={1}
															style={{ wordBreak: 'break-all' }}
														>
															{loanDefinition.name}
														</TextMantine>
													</TooltipMantine>
												</Stack>
											}
											sectionSeparator={isFirst}
											sectionLabel={
												isFirst
													? tMipManage('new_leaver.steps.investors.loans')
													: undefined
											}
											childrenAlign="left"
											sectionIcon="bank"
										>
											<TextMantine
												size="B6Medium"
												tt="uppercase"
												c={defaultTheme.v2.color.neutral[600]}
											>
												{tMipManage('new_leaver.steps.investors.amount')}
											</TextMantine>
										</HeaderCell>
										<HeaderCell size={1.9} childrenAlign="left">
											<TextMantine
												size="B6Medium"
												tt="uppercase"
												c={defaultTheme.v2.color.neutral[600]}
											>
												{tMipManage('new_leaver.steps.investors.interest')}
											</TextMantine>
										</HeaderCell>
									</React.Fragment>
								);
							})
						: null}

					{mainContracts?.map((contract, index) => {
						const isFirst = index === 0;
						const totalAssigned = investorsList?.filter((investor) =>
							investor.contracts?.find(
								(investorContractId) => investorContractId === contract.id
							)
						)?.length;
						return (
							<HeaderCell
								size={2}
								groupAlign="center"
								childrenAlign="center"
								sectionSeparator={isFirst}
								sectionIcon="paper"
								groupComponent={
									totalAssigned ? (
										<TableHeaderContractCellGroup>
											<ChipsV2
												size="s"
												color={COLOR.GREEN}
												chipTheme={CHIP_THEME.LIGHT}
												prefixIcon="checkmark-circle-contained-filled"
											>
												{totalAssigned || undefined}
											</ChipsV2>
										</TableHeaderContractCellGroup>
									) : (
										<div />
									)
								}
								sectionLabel={
									isFirst
										? tMipCreate('investors.table.header.contracts_documents')
										: undefined
								}
								key={contract.id}
							>
								{investorsList?.length ? (
									<TableHeaderContractCellGroup>
										<UIElementIdentifier
											identifier={
												TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_SELECT_ALL_CONTRACTS
											}
										>
											<TableCheckbox
												onClick={() => {
													onSelectAllClicked(contract);
												}}
												checked={
													contractsAllSelection.find(
														(itemContract) =>
															itemContract.contractID === contract.id
													)?.isChecked ?? false
												}
											/>
										</UIElementIdentifier>
										<TextWithTooltip
											mainText={contract.name}
											tooltipText={contract.name}
											textProps={{
												colorFaded: true,
												variant: 'overline',
												uppercase: true,
											}}
										/>
									</TableHeaderContractCellGroup>
								) : (
									<TextWithTooltip
										mainText={contract.name}
										tooltipText={contract.name}
										textProps={{
											colorFaded: true,
											variant: 'overline',
											uppercase: true,
										}}
									/>
								)}
							</HeaderCell>
						);
					})}

					{!!sideAgreements?.length && (
						<HeaderCell
							customSize="minmax(10rem, 1fr)"
							groupAlign="right"
							childrenAlign="right"
							groupComponent={
								totalSideAgreementsAssigned ? (
									<div>
										<ChipsV2
											size="s"
											color={COLOR.GREEN}
											chipTheme={CHIP_THEME.LIGHT}
											prefixIcon="checkmark-circle-contained-filled"
										>
											{totalSideAgreementsAssigned || undefined}
										</ChipsV2>
									</div>
								) : null
							}
						>
							{tMipCreate('investors.table.header.side_agreements')}
						</HeaderCell>
					)}
				</HeaderRow>
				<BodyRow>
					{investorsList?.map((investor, index) => {
						const isRowInvalid = investor.securities.every(
							({ totalNumber }) => !totalNumber
						);

						const rowPopoverMessage = isRowInvalid
							? tMipManage('new_leaver.steps.investors.no_sold_number')
							: undefined;

						return (
							<Row
								key={`${investor.email}${index}`}
								isInvalid={isRowInvalid}
								popoverMessage={rowPopoverMessage}
							>
								<TextCell text={investor.firstName} elipsis />
								<TextCell text={investor.lastName} elipsis />
								<TextCell text={investor.email} elipsis />

								{securitiesList?.map((security) => {
									const investorSecurityData = investor.securities?.find(
										(investorSecurity) =>
											investorSecurity.securityId === security.id
									);

									const isNumberCellInvalid =
										investorSecurityData &&
										investorSecurityData.totalNumber >
											investorSecurityData.currentHoldings;
									return (
										<React.Fragment key={security.id}>
											<Cell align="center">
												<Text variant="p-sm">
													<NumberFormatted
														value={
															investorSecurityData?.currentHoldings
														}
													/>
												</Text>
											</Cell>
											<Cell
												align="center"
												withPopover={isNumberCellInvalid}
												isInvalid={isNumberCellInvalid}
												popoverProps={{
													content: (
														<Text variant="p-sm">
															The sold number is bigger than the
															investor's current holdings
														</Text>
													),
												}}
											>
												<Text variant="p-sm">
													<NumberFormatted
														value={investorSecurityData?.totalNumber}
													/>
												</Text>
											</Cell>
											<Cell
												align={
													!investorSecurityData?.totalCost || !currency
														? 'center'
														: 'right'
												}
												isInvalid={
													!!investorSecurityData?.totalNumber &&
													!investorSecurityData?.totalCost
												}
											>
												<Text variant="p-sm">
													<AmountFormatted
														value={investorSecurityData?.totalCost}
														currency={currency}
													/>
												</Text>
											</Cell>
										</React.Fragment>
									);
								})}

								{loanDefinitions?.map((loanDefinition) => {
									const investorLoanData = investor.loans?.find(
										(investorLoan) =>
											investorLoan.loanDefinitionId === loanDefinition.id
									);

									return (
										<React.Fragment key={loanDefinition.id}>
											<Cell align="center">
												<Text variant="p-sm">
													<AmountFormatted
														value={investorLoanData?.amount}
														currency={currency}
													/>
												</Text>
											</Cell>
											<Cell align="center">
												<Text variant="p-sm">
													<AmountFormatted
														value={investorLoanData?.amountRemaining}
														currency={currency}
														showZero
													/>
												</Text>
											</Cell>
										</React.Fragment>
									);
								})}

								{mainContracts?.map((contract) => {
									const isChecked = !!investor.contracts?.find(
										(investorContractId) => investorContractId === contract.id
									);

									const onClick = () => {
										const updatedContracts: string[] = isChecked
											? investor.contracts?.filter(
													(investorContractId) =>
														investorContractId !== contract?.id
												) ?? []
											: [...(investor.contracts || []), contract.id];
										onUpdateInvestorContracts(updatedContracts, investor.id);
									};

									return (
										<Cell align="left" key={contract.id}>
											<UIElementIdentifier
												identifier={
													TEST_ELEMENT_IDS.MIP_CREATE_INVESTORS_SELECT_CONTRACT
												}
												index={index}
											>
												<TableCheckbox
													onClick={onClick}
													checked={isChecked}
												/>
											</UIElementIdentifier>
										</Cell>
									);
								})}

								{!!sideAgreements?.length && (
									<Cell align="right">
										<SideAgreementsPopup
											onUpdateInvestorContracts={onUpdateInvestorContracts}
											sideAgreements={sideAgreements}
											investor={investor}
											index={index}
										/>
									</Cell>
								)}
							</Row>
						);
					})}
				</BodyRow>
			</DefaultTable>
		</div>
	);
};

const TextCell = ({
	text,
	align,
	...props
}: { text: string | number; align?: Align } & ITextProps) => {
	const isInvalid = useMemo(() => {
		return isInvalidTableTextCell(text);
	}, [text]);

	return (
		<Cell align={align} isInvalid={isInvalid}>
			<Text {...props} variant="p-sm">
				{text}
			</Text>
		</Cell>
	);
};

const Memoized = memo(NewLeaversInvestorsTable);
const Styled = styled(Memoized)`
	${Cell}:not(:last-of-type) {
		padding-right: 1em;
	}

	${TableHeaderContractCellGroup} {
		${Tooltip} {
			margin-left: 0.5em;
		}
	}

	${HeaderRow} {
		${HeaderCell}:nth-child(1),
		${HeaderCell}:nth-child(2),
		${HeaderCell}:nth-child(3) {
			position: sticky;
			background: white;
			z-index: 10;
		}

		${HeaderCell}:nth-child(1) {
			left: 0.5em;

			&::after,
			&::before {
				content: '';
				position: absolute;
				top: 0;
				height: 100%;
				background: white;
				pointer-events: none;
				z-index: 11;
			}

			&::after {
				right: -1em;
				width: 1em;
			}

			&::before {
				left: -0.5em;
				width: 0.5em;
			}
		}

		${HeaderCell}:nth-child(2) {
			left: calc(2 * 4rem + 1em);
		}
		${HeaderCell}:nth-child(3) {
			left: calc(4 * 4rem + 1em);
		}

		${HeaderCell}:nth-child(4) {
			margin-left: 1em;
		}
	}

	${Row} {
		${Cell}:nth-child(1),
		${Cell}:nth-child(2),
		${Cell}:nth-child(3) {
			position: sticky;
			background: ${({ theme }) => theme.v2.color.neutral[100]};
			z-index: 9;
		}

		${Cell}:nth-child(1) {
			left: 0.5em;

			&::after,
			&::before {
				content: '';
				position: absolute;
				top: 0;
				height: 100%;
				background: ${({ theme }) => theme.v2.color.neutral[100]};
				pointer-events: none;
				z-index: 10;
			}

			&::after {
				right: -1em;
				width: 1em;
			}

			&::before {
				left: -0.5em;
				width: 0.5em;
			}
		}

		${Cell}:nth-child(2) {
			left: calc(2 * 4rem + 1em);
		}
		${Cell}:nth-child(3) {
			left: calc(4 * 4rem + 1em);
		}
	}
`;

export { Styled as NewLeaversInvestorsTable };
